/**
 * O `useAuth` do painel, servido pela sessão do app unificado (A019).
 *
 * As páginas do admin importam `@app/providers/AuthProvider`; no app esse
 * caminho resolve para cá. O `user` tem a forma legada que elas conhecem
 * (`role` + `permissionsPresets` + `partnerId`), derivada do CONTEXTO
 * assumido — não do documento da conta, que pode ser paciente enquanto o
 * vínculo é de mantenedora.
 *
 * O cliente HTTP do admin continua o dele, lendo `eva_admin_token`: o
 * provider abaixo espelha a sessão nessas chaves e as mantém em dia.
 */
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { caminhoDeSaida } from '../../../app/sair';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { brandRoleToLegacy, isEvaBrand, normalizeBrandId } from '@eva/schemas';
import { domainConfig, sessionStore, useBrand, useSession, type SessionContext, type SessionUser } from '@eva/domain';

export const CHAVES_DO_ADMIN = {
  token: 'eva_admin_token',
  refresh: 'eva_admin_refresh_token',
  user: 'eva_admin_user',
  brand: 'admin:brand',
  issuedAt: 'auth_access_issued_at',
} as const;

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  permissionsPresets?: string | string[];
  partnerId?: string | null;
  medicalProviderId?: string | null;
  companyId?: string | null;
  forcePasswordChange?: boolean;
}

export function usuarioLegado(user: SessionUser, context: SessionContext): AdminUser {
  const legado = brandRoleToLegacy(context.role);
  return {
    ...user,
    role: legado.role,
    permissionsPresets: legado.permissionsPresets,
    // A marca da sessão, a Eva inclusive ('default'): o painel trata toda
    // marca pelo mesmo caminho (fase 5). Só a mantenedora não tem marca.
    partnerId: context.role === 'MAINTAINER' ? undefined : normalizeBrandId(context.brandId),
    medicalProviderId: context.medicalProviderId ?? user.medicalProviderId ?? null,
    companyId: context.companyId ?? null,
  };
}

/** Espelha a sessão nas chaves que o cliente HTTP do admin lê. */
export function espelharSessaoParaOAdmin(user: SessionUser, context: SessionContext): void {
  const { accessToken, refreshToken } = sessionStore.readTokens();
  if (!accessToken || !refreshToken) return;
  try {
    localStorage.setItem(CHAVES_DO_ADMIN.token, accessToken);
    localStorage.setItem(CHAVES_DO_ADMIN.refresh, refreshToken);
    localStorage.setItem(CHAVES_DO_ADMIN.user, JSON.stringify(usuarioLegado(user, context)));
    localStorage.setItem(CHAVES_DO_ADMIN.issuedAt, String(Date.now()));
    if (isEvaBrand(context.brandId)) localStorage.removeItem(CHAVES_DO_ADMIN.brand);
    else localStorage.setItem(CHAVES_DO_ADMIN.brand, JSON.stringify({ type: 'id', value: context.brandId }));
    localStorage.removeItem('eva_redirect_after_login');
  } catch {
    /* sem storage o cliente do admin vai pedir login; não há o que fazer */
  }
}

export function limparSessaoDoAdmin(): void {
  try {
    for (const k of Object.values(CHAVES_DO_ADMIN)) localStorage.removeItem(k);
  } catch {
    /* idem */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { brand } = useBrand();
  const s = useSession();
  useEffect(() => {
    if (s.status === 'ready' && s.user && s.context) espelharSessaoParaOAdmin(s.user, s.context);
  }, [s.status, s.user, s.context]);

  // O cliente do admin, ao renovar num 401, avisa por este evento; e ao
  // falhar de vez chama __authForceLogout. Os dois passam a falar com a
  // sessão do app.
  const forceLogout = useCallback(
    (reason?: string) => {
      limparSessaoDoAdmin();
      notifications.show({ color: 'orange', title: 'Sessão encerrada', message: reason ?? 'Entre de novo.' });
      void s.logout().finally(() => window.location.assign(caminhoDeSaida(brand.slug)));
    },
    [s, brand.slug]
  );
  useEffect(() => {
    (window as any).__authForceLogout = forceLogout;
    const aoRenovar = (e: Event) => {
      const d = (e as CustomEvent).detail ?? {};
      if (d.accessToken && d.refreshToken) sessionStore.writeTokens({ accessToken: d.accessToken, refreshToken: d.refreshToken });
    };
    const aoTrocarEmOutraAba = (e: StorageEvent) => {
      if (e.key === `${domainConfig().storagePrefix}_context` && e.oldValue !== e.newValue) window.location.replace('/marca');
    };
    window.addEventListener('storage', aoTrocarEmOutraAba);
    window.addEventListener('auth:tokenRefreshed', aoRenovar);
    return () => {
      delete (window as any).__authForceLogout;
      window.removeEventListener('auth:tokenRefreshed', aoRenovar);
      window.removeEventListener('storage', aoTrocarEmOutraAba);
    };
  }, [forceLogout]);

  return <>{children}</>;
}

export function useAuth(): {
  user: AdminUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: (showNotification?: boolean) => void;
  forceLogout: (reason?: string) => void;
} {
  const s = useSession();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const user = useMemo(() => (s.status === 'ready' && s.user && s.context ? usuarioLegado(s.user, s.context) : null), [s.status, s.user, s.context]);

  const logout = useCallback(
    (showNotification = false) => {
      limparSessaoDoAdmin();
      if (showNotification) notifications.show({ color: 'blue', message: 'Você saiu.' });
      void s.logout().then(() => navigate(caminhoDeSaida(brand.slug), { replace: true }));
    },
    [s, navigate, brand.slug]
  );

  const forceLogout = useCallback(
    (reason?: string) => {
      limparSessaoDoAdmin();
      notifications.show({ color: 'orange', title: 'Sessão encerrada', message: reason ?? 'Entre de novo.' });
      void s.logout().then(() => navigate(caminhoDeSaida(brand.slug), { replace: true }));
    },
    [s, navigate, brand.slug]
  );

  return {
    user,
    isAuthenticated: s.status === 'ready',
    login: async () => {
      throw new Error('O login é feito pela porta do app (/entrar).');
    },
    logout,
    forceLogout,
  };
}
